/**
 * Is this order line an orthopaedic sandal, and what insole top cover (if
 * any) may be shown for it.
 *
 * WHY THIS EXISTS. The wizard seeds a DEFAULT insole design branch
 * (`deep.design`: top cover "EVA 2mm" / "DryFeet" / "Black") and
 * buildLineConfiguration serialises the whole deep state for every line
 * type, so sandal lines created since 2026-07-26 STORE an insole top cover
 * the podiatrist never saw or chose (27 of 139 sandal lines on 2026-09-17).
 * Any surface that summarises `deep.design` without asking what the line is
 * therefore prints "Top cover: Adult · EVA 2mm · DryFeet · Black" on a
 * sandal. The sandal's own cover colour lives at `deep.sandal.top_cover` and
 * is already part of the sandal summary.
 *
 * Both clauses are required: `line_type` is 'orthopedic_sandal' (US
 * spelling) and `solution_subtype` is 'orthopaedic_sandal' (UK spelling),
 * and legacy sandals stored with line_type 'custom_insole' exist. This is
 * the same two-clause rule the prescription Excel exporter already gates on.
 */
import { summarizeTopCover } from './wizard/prescription/summary';
import type { InsoleDesignState } from './wizard/prescription/prescriptionState';

export interface SandalLineLike {
  line_type?: string | null | undefined;
  solution_subtype?: string | null | undefined;
}

export function isSandalLine(line: SandalLineLike): boolean {
  return line.solution_subtype === 'orthopaedic_sandal' || line.line_type === 'orthopedic_sandal';
}

/**
 * The insole top cover summary for a line, or `null` when the line is a
 * sandal: a sandal never shows an insole top cover, whatever is stored on it.
 * Callers render the "Top cover" row only when this is not null.
 */
export function insoleTopCoverFor(
  line: SandalLineLike,
  design: InsoleDesignState | undefined,
): string | null {
  if (isSandalLine(line)) return null;
  return summarizeTopCover(design);
}
